'use client';

import React from 'react';

import {ClpCourseProvider, TrackedCourse} from './context/clp-course-provider.react-component';
import {CLPPurchaseContextProvider} from './context/clp-purchase-context.react-component';
import {
    DiscountCodeProvider,
    useDiscountCodeProvider,
} from './context/discount-code-provider.react-component';
import {ClpContextProvider} from './context/clp-context-provider.react-component';
import {CoursePriceProvider} from '@udemy-v2/react-course-pricing';
import {CourseWithInstructorDetailsProvider} from './context/course-with-instructor-details-provider.react-component';
import {PriceEventTrackingProvider} from '@udemy-v2/react-currency-formatting';
import {GwgAccelerator} from '@udemy-v2/career-accelerators';
import {CourseCareerAcceleratorProvider} from './context/career-accelerator-provider.react-component';
import {CLPVoucherContextProvider} from './context/voucher-context.react-component';
import {AllGwgAcceleratorsContext} from './context/all-gwg-accelerators-context';

import {CareerAcceleratorsByCourseIds} from '@udemy-v2/career-accelerators';
import {CourseLandingPage} from './course-landing-page.react-component';
import {
    CourseSubscriptionPlansProvider,
    SubscriptionPlanProductType,
    SubscriptionPlansProvider,
} from '@udemy-v2/subscription-graphql';
import {CourseSeoPricing} from '@/features/course-landing-page/components/course-seo/course-seo-pricing.react-component';
import {CoursePreviewVideoProvider} from '@/features/course-landing-page/components/course-preview/context';
import {SmartBarStoreProvider} from '@udemy-v2/react-smart-bar';
import {AssetImageProvider} from './context/asset-image-provider.react-component';
import {AssetImage} from './components/course-preview-entry-point/course-preview-entry-point.react-component';
import {ClpSubscriptionContextProvider} from './context/clp-subscription-context.react-component';
import {ClpNoticeContextProvider} from './context/clp-notice-context-provider.react-component';
import {ClpGtmDataLayer} from './components/google-tag-manager/clp-gtm-data-layer.react-component';
import {useFeatures} from '@udemy-v2/react-experimentation';

/**
 * Subscription plan product types relevant for CLP GrowWithGoogle offerings
 */
const CLP_GROW_WITH_GOOGLE_SUBSCRIPTION_PLAN_TYPES = [
    SubscriptionPlanProductType.GrowWithGoogle,
    SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle,
];

interface CourseLandingPageWithProvidersProps {
    course: TrackedCourse;
    courseCareerAccelerator?: CareerAcceleratorsByCourseIds;
    allGwgAccelerators?: GwgAccelerator[];
}

/**
 * Wrapper component that passes discountCodeProvider to CoursePriceProvider
 */
function CoursePriceProviderWithDiscount({children}: {children: React.ReactNode}) {
    const {discountCodeProvider} = useDiscountCodeProvider();

    return (
        <CoursePriceProvider discountCodeProvider={discountCodeProvider}>
            {children}
        </CoursePriceProvider>
    );
}

/**
 * Inner component that sets up the provider hierarchy
 */
function CourseLandingPageWithCoursePriceProvider(props: CourseLandingPageWithProvidersProps) {
    const {course, courseCareerAccelerator, allGwgAccelerators} = props;

    const preloadedAssetImage = <AssetImage images={course.images} />;

    // Set up subscription plan query variables for GrowWithGoogle offerings
    const marketingURL =
        typeof window !== 'undefined' ? window.location.href : 'https://www.udemy.com';
    const subscriptionPlanQueryVariables = [
        ...CLP_GROW_WITH_GOOGLE_SUBSCRIPTION_PLAN_TYPES.map((productType) => ({
            filters: {productType},
            marketingURL,
        })),
        {
            filters: {productType: SubscriptionPlanProductType.Consumersubscription},
            marketingURL,
        },
    ];

    const {featureVariantAssignments} = useFeatures(['blended_monetization_promo']);
    const blendedMonthlyEnabled =
        (featureVariantAssignments.get('blended_monetization_promo')?.configuration
            ?.blended_monthly_enabled ?? false) as boolean;
    const blendedMonthlyDiscountCode = featureVariantAssignments
        .get('blended_monetization_promo')
        ?.configuration?.monthly_discount_code as string | undefined;

    const courseSubscriptionPlanQueryVariables = {
        filters: {productType: SubscriptionPlanProductType.Consumersubscription},
        marketingURL,
        couponCode: blendedMonthlyEnabled ? blendedMonthlyDiscountCode : undefined,
    };

    return (
        <SubscriptionPlansProvider subscriptionPlanQueryVariables={subscriptionPlanQueryVariables}>
            <CourseSubscriptionPlansProvider
                courseId={String(course.id)}
                courseLandingUrl={course.urlCourseLanding ?? ''}
                subscriptionPlanQueryVariables={courseSubscriptionPlanQueryVariables}
            >
                <ClpSubscriptionContextProvider courseId={String(course.id)}>
                    <ClpNoticeContextProvider>
                        <ClpCourseProvider course={course}>
                            <AllGwgAcceleratorsContext.Provider value={allGwgAccelerators}>
                                <CourseCareerAcceleratorProvider
                                    courseCareerAccelerator={courseCareerAccelerator}
                                >
                                    <DiscountCodeProvider>
                                        <CoursePriceProviderWithDiscount>
                                            <ClpGtmDataLayer />
                                            <CLPPurchaseContextProvider>
                                                <CLPVoucherContextProvider>
                                                    <AssetImageProvider
                                                        preloadedAssetImage={preloadedAssetImage}
                                                    >
                                                        <CoursePreviewVideoProvider>
                                                            <ClpContextProvider>
                                                                <PriceEventTrackingProvider
                                                                    priceEventTrackingContext={{
                                                                        context: 'landing-page',
                                                                    }}
                                                                >
                                                                    <CourseWithInstructorDetailsProvider
                                                                        instructors={
                                                                            course.instructors
                                                                        }
                                                                    >
                                                                        <SmartBarStoreProvider
                                                                            config={{
                                                                                forceNoticeType:
                                                                                    'smart_bar',
                                                                                showSmartBar: true,
                                                                            }}
                                                                            data={null}
                                                                        >
                                                                            <CourseSeoPricing
                                                                                course={course}
                                                                            />
                                                                            <CourseLandingPage />
                                                                        </SmartBarStoreProvider>
                                                                    </CourseWithInstructorDetailsProvider>
                                                                </PriceEventTrackingProvider>
                                                            </ClpContextProvider>
                                                        </CoursePreviewVideoProvider>
                                                    </AssetImageProvider>
                                                </CLPVoucherContextProvider>
                                            </CLPPurchaseContextProvider>
                                        </CoursePriceProviderWithDiscount>
                                    </DiscountCodeProvider>
                                </CourseCareerAcceleratorProvider>
                            </AllGwgAcceleratorsContext.Provider>
                        </ClpCourseProvider>
                    </ClpNoticeContextProvider>
                </ClpSubscriptionContextProvider>
            </CourseSubscriptionPlansProvider>
        </SubscriptionPlansProvider>
    );
}

export function CourseLandingPageWithProviders(props: CourseLandingPageWithProvidersProps) {
    return <CourseLandingPageWithCoursePriceProvider {...props} />;
}
